import { useState } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { 
  Search, 
  MapPin, 
  Users, 
  Package,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Navigation,
  Bike,
  Phone,
  Building2,
  Bell
} from "lucide-react";

const OrderDebugPage = () => {
  const [loading, setLoading] = useState(false);
  
  // Order lookup state
  const [orderId, setOrderId] = useState("");
  const [orderResult, setOrderResult] = useState(null);
  
  // Notification debug state
  const [notifOrderId, setNotifOrderId] = useState("");
  const [notifResult, setNotifResult] = useState(null);
  
  // Visibility debug state
  const [visOrderId, setVisOrderId] = useState("");
  const [visAdminId, setVisAdminId] = useState("");
  const [visResult, setVisResult] = useState(null);
  
  // Search state
  const [searchParams, setSearchParams] = useState({
    customer_name: "",
    restaurant_name: "",
    city_name: "",
    phone: ""
  });
  const [searchResults, setSearchResults] = useState(null);
  
  // City check state
  const [cityCheckCoords, setCityCheckCoords] = useState({ lat: "", lng: "" });
  const [cityCheckResult, setCityCheckResult] = useState(null);
  
  // Rider check state
  const [riderCheckCoords, setRiderCheckCoords] = useState({ lat: "", lng: "", radius: "15" });
  const [riderCheckResult, setRiderCheckResult] = useState(null);

  const lookupOrder = async () => {
    if (!orderId.trim()) {
      toast.error("Please enter an order ID");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quick-commerce/debug/order/${orderId.trim()}`);
      setOrderResult(res.data);
    } catch (error) {
      toast.error("Failed to lookup order");
      setOrderResult({ found: false, message: error.response?.data?.detail || "Error" });
    } finally {
      setLoading(false);
    }
  };

  const debugNotification = async () => {
    if (!notifOrderId.trim()) {
      toast.error("Please enter an order ID");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quick-commerce/debug/order-notification/${notifOrderId.trim()}`);
      setNotifResult(res.data);
    } catch (error) {
      toast.error("Failed to debug notification");
      setNotifResult({ error: error.response?.data?.detail || "Error" });
    } finally {
      setLoading(false);
    }
  };

  const debugVisibility = async () => {
    if (!visOrderId.trim() || !visAdminId.trim()) {
      toast.error("Please enter both order ID and admin ID");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/${visAdminId.trim()}/debug/order-visibility/${visOrderId.trim()}`);
      setVisResult(res.data);
    } catch (error) {
      toast.error("Failed to check visibility");
      setVisResult({ error: error.response?.data?.detail || "Error" });
    } finally {
      setLoading(false);
    }
  };

  const searchOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchParams.customer_name) params.append("customer_name", searchParams.customer_name);
      if (searchParams.restaurant_name) params.append("restaurant_name", searchParams.restaurant_name);
      if (searchParams.city_name) params.append("city_name", searchParams.city_name);
      if (searchParams.phone) params.append("phone", searchParams.phone);
      params.append("limit", "50");
      
      const res = await axios.get(`${API}/quick-commerce/debug/search?${params.toString()}`);
      setSearchResults(res.data);
    } catch (error) {
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  const checkCity = async () => {
    if (!cityCheckCoords.lat || !cityCheckCoords.lng) {
      toast.error("Please enter coordinates");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quick-commerce/debug/city-check?lat=${cityCheckCoords.lat}&lng=${cityCheckCoords.lng}`);
      setCityCheckResult(res.data);
    } catch (error) {
      toast.error("City check failed");
    } finally {
      setLoading(false);
    }
  };

  const checkRiders = async () => {
    if (!riderCheckCoords.lat || !riderCheckCoords.lng) {
      toast.error("Please enter coordinates");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quick-commerce/debug/riders?lat=${riderCheckCoords.lat}&lng=${riderCheckCoords.lng}&radius_km=${riderCheckCoords.radius || 15}`);
      setRiderCheckResult(res.data);
    } catch (error) {
      toast.error("Rider check failed");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      "Ride Placed": "bg-blue-500",
      "Ride Accepted": "bg-yellow-500",
      "Ride InProgress": "bg-orange-500",
      "picked_up": "bg-purple-500",
      "Ride Completed": "bg-green-500",
      "Ride Canceled": "bg-red-500"
    };
    return colors[status] || "bg-gray-500";
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-3">
        <Search className="w-8 h-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Order Debug Tools</h1>
          <p className="text-muted-foreground">Troubleshoot missing orders, city assignments, and rider availability</p>
        </div>
      </div>

      <Tabs defaultValue="lookup" className="space-y-4">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="lookup" data-testid="tab-lookup">
            <Package className="w-4 h-4 mr-2" />
            Order Lookup
          </TabsTrigger>
          <TabsTrigger value="visibility" data-testid="tab-visibility">
            <XCircle className="w-4 h-4 mr-2" />
            Why Can't Admin See?
          </TabsTrigger>
          <TabsTrigger value="notification" data-testid="tab-notification">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Why No Notification?
          </TabsTrigger>
          <TabsTrigger value="search" data-testid="tab-search">
            <Search className="w-4 h-4 mr-2" />
            Search Orders
          </TabsTrigger>
          <TabsTrigger value="city" data-testid="tab-city">
            <MapPin className="w-4 h-4 mr-2" />
            City Check
          </TabsTrigger>
          <TabsTrigger value="riders" data-testid="tab-riders">
            <Users className="w-4 h-4 mr-2" />
            Rider Check
          </TabsTrigger>
        </TabsList>

        {/* Order Lookup Tab */}
        <TabsContent value="lookup">
          <Card>
            <CardHeader>
              <CardTitle>Lookup Order by ID</CardTitle>
              <CardDescription>Enter an order ID or ride ID to check its details and diagnose issues</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Enter order ID or ride ID..."
                  value={orderId}
                  onChange={(e) => setOrderId(e.target.value)}
                  data-testid="order-id-input"
                  className="flex-1"
                />
                <Button onClick={lookupOrder} disabled={loading} data-testid="lookup-order-btn">
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  <span className="ml-2">Lookup</span>
                </Button>
              </div>

              {orderResult && (
                <div className="mt-4 space-y-4">
                  {orderResult.found ? (
                    <>
                      {/* Issues Alert */}
                      {orderResult.issues?.length > 0 && (
                        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                          <div className="flex items-center gap-2 text-red-600 dark:text-red-400 font-medium mb-2">
                            <AlertTriangle className="w-5 h-5" />
                            Found {orderResult.issues.length} Issue(s)
                          </div>
                          <ul className="list-disc list-inside text-sm text-red-600 dark:text-red-400">
                            {orderResult.issues.map((issue, i) => (
                              <li key={i}>{issue}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Order Details */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <h3 className="font-semibold">Order Info</h3>
                          <div className="text-sm space-y-1">
                            <p><span className="text-muted-foreground">ID:</span> {orderResult.order.id}</p>
                            <p><span className="text-muted-foreground">Ride ID:</span> {orderResult.order.ride_id}</p>
                            <p className="flex items-center gap-2">
                              <span className="text-muted-foreground">Status:</span>
                              <Badge className={getStatusColor(orderResult.order.status)}>
                                {orderResult.order.status}
                              </Badge>
                            </p>
                            <p><span className="text-muted-foreground">Amount:</span> ₹{orderResult.order.offerRate}</p>
                            <p><span className="text-muted-foreground">Created:</span> {new Date(orderResult.order.createdAt || orderResult.order.created_at).toLocaleString()}</p>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <h3 className="font-semibold">Location Info</h3>
                          <div className="text-sm space-y-1">
                            <p><span className="text-muted-foreground">Pickup:</span> {orderResult.order.sourceLocationName}</p>
                            <p><span className="text-muted-foreground">Delivery:</span> {orderResult.order.destinationLocationName}</p>
                            <p><span className="text-muted-foreground">City:</span> {orderResult.city_info?.name || orderResult.order.city_name || "Not assigned"}</p>
                            <p><span className="text-muted-foreground">Distance:</span> {orderResult.order.distance} {orderResult.order.distanceType}</p>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <h3 className="font-semibold">Customer</h3>
                          <div className="text-sm space-y-1">
                            <p><span className="text-muted-foreground">Name:</span> {orderResult.order.customerName || orderResult.order.customer?.name}</p>
                            <p><span className="text-muted-foreground">Phone:</span> {orderResult.order.customerPhone || orderResult.order.customer?.phone}</p>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <h3 className="font-semibold">Driver</h3>
                          <div className="text-sm space-y-1">
                            {orderResult.order.driverId ? (
                              <>
                                <p><span className="text-muted-foreground">Name:</span> {orderResult.driver_info?.name || orderResult.order.driverName}</p>
                                <p><span className="text-muted-foreground">Phone:</span> {orderResult.driver_info?.phone || orderResult.order.driverPhone}</p>
                              </>
                            ) : (
                              <p className="text-yellow-600">No driver assigned yet</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                      <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400">
                        <XCircle className="w-5 h-5" />
                        <span>{orderResult.message}</span>
                      </div>
                      {orderResult.suggestion && (
                        <p className="text-sm text-muted-foreground mt-2">{orderResult.suggestion}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Visibility Debug Tab */}
        <TabsContent value="visibility">
          <Card>
            <CardHeader>
              <CardTitle>Why Can't Admin See This Order?</CardTitle>
              <CardDescription>Debug why an order isn't showing in an admin's Food Delivery tab</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Order ID</Label>
                  <Input
                    placeholder="e.g., QC-BA1705-1"
                    value={visOrderId}
                    onChange={(e) => setVisOrderId(e.target.value)}
                    data-testid="vis-order-id-input"
                  />
                </div>
                <div>
                  <Label>Admin ID (or phone)</Label>
                  <Input
                    placeholder="e.g., admin-xxx or 9515141593"
                    value={visAdminId}
                    onChange={(e) => setVisAdminId(e.target.value)}
                    data-testid="vis-admin-id-input"
                  />
                </div>
              </div>
              
              <Button onClick={debugVisibility} disabled={loading} data-testid="debug-visibility-btn">
                {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                Check Visibility
              </Button>

              {visResult && !visResult.error && (
                <div className="mt-4 space-y-4">
                  {/* Diagnosis */}
                  <div className={`p-4 rounded-lg ${visResult.diagnosis?.startsWith("✅") ? "bg-green-50 dark:bg-green-900/20 border-green-200" : "bg-red-50 dark:bg-red-900/20 border-red-200"} border`}>
                    <p className="font-medium">{visResult.diagnosis}</p>
                    {visResult.fix_suggestion && (
                      <p className="text-sm text-muted-foreground mt-2">
                        <strong>Fix:</strong> {visResult.fix_suggestion}
                      </p>
                    )}
                  </div>

                  {/* Admin & Order Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Admin Info</h3>
                      <div className="text-sm space-y-1">
                        <p><span className="text-muted-foreground">Name:</span> {visResult.admin?.name}</p>
                        <p><span className="text-muted-foreground">Phone:</span> {visResult.admin?.phone}</p>
                        <p><span className="text-muted-foreground">Role:</span> <Badge>{visResult.admin?.role}</Badge></p>
                        <p><span className="text-muted-foreground">Owned Cities:</span> {visResult.admin?.owned_cities_count}</p>
                        <p className="text-xs">{visResult.admin?.owned_cities?.join(", ") || "None"}</p>
                        <p><span className="text-muted-foreground">Drivers:</span> {visResult.admin?.drivers_count}</p>
                      </div>
                    </div>

                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Order Info</h3>
                      <div className="text-sm space-y-1">
                        <p><span className="text-muted-foreground">ID:</span> {visResult.order?.id}</p>
                        <p><span className="text-muted-foreground">Status:</span> <Badge className={getStatusColor(visResult.order?.status)}>{visResult.order?.status}</Badge></p>
                        <p><span className="text-muted-foreground">City:</span> {visResult.order?.city_name || "Not set"}</p>
                        <p><span className="text-muted-foreground">Driver:</span> {visResult.order?.driver_id || "Not assigned"}</p>
                      </div>
                      
                      {visResult.order_city_owner && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-sm font-medium">City Owner:</p>
                          <p className="text-sm">{visResult.order_city_owner.owner_name} ({visResult.order_city_owner.owner_phone})</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Visibility Check Details */}
                  <div className="p-4 border rounded-lg">
                    <h3 className="font-semibold mb-2">Visibility Check</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div className={`p-2 rounded text-center ${visResult.visibility_check?.is_superadmin ? "bg-green-100" : "bg-gray-100"}`}>
                        <p className="text-xs text-muted-foreground">Is Superadmin</p>
                        <p className="font-medium">{visResult.visibility_check?.is_superadmin ? "Yes" : "No"}</p>
                      </div>
                      <div className={`p-2 rounded text-center ${visResult.visibility_check?.city_matches_admin ? "bg-green-100" : "bg-gray-100"}`}>
                        <p className="text-xs text-muted-foreground">City Match</p>
                        <p className="font-medium">{visResult.visibility_check?.city_matches_admin ? "Yes" : "No"}</p>
                      </div>
                      <div className={`p-2 rounded text-center ${visResult.visibility_check?.driver_matches_admin ? "bg-green-100" : "bg-gray-100"}`}>
                        <p className="text-xs text-muted-foreground">Driver Match</p>
                        <p className="font-medium">{visResult.visibility_check?.driver_matches_admin ? "Yes" : "No"}</p>
                      </div>
                      <div className={`p-2 rounded text-center ${visResult.visibility_check?.would_be_visible ? "bg-green-100" : "bg-red-100"}`}>
                        <p className="text-xs text-muted-foreground">Visible</p>
                        <p className="font-medium">{visResult.visibility_check?.would_be_visible ? "Yes" : "No"}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {visResult?.error && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <p className="text-red-600">{visResult.error}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notification Debug Tab */}
        <TabsContent value="notification">
          <Card>
            <CardHeader>
              <CardTitle>Why No Rider Notification?</CardTitle>
              <CardDescription>Debug why an order might not have sent notifications to riders</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Enter order ID (e.g., QC-BA1705-1)..."
                  value={notifOrderId}
                  onChange={(e) => setNotifOrderId(e.target.value)}
                  data-testid="notif-order-id-input"
                  className="flex-1"
                />
                <Button onClick={debugNotification} disabled={loading} data-testid="debug-notif-btn">
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  <span className="ml-2">Analyze</span>
                </Button>
              </div>

              {notifResult && !notifResult.error && (
                <div className="mt-4 space-y-4">
                  {/* Diagnosis */}
                  <div className={`p-4 rounded-lg ${notifResult.diagnosis?.startsWith("✅") ? "bg-green-50 dark:bg-green-900/20 border-green-200" : "bg-red-50 dark:bg-red-900/20 border-red-200"} border`}>
                    <p className="font-medium">{notifResult.diagnosis}</p>
                  </div>

                  {/* Order Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Order Info</h3>
                      <div className="text-sm space-y-1">
                        <p><span className="text-muted-foreground">ID:</span> {notifResult.order?.id}</p>
                        <p><span className="text-muted-foreground">Status:</span> <Badge className={getStatusColor(notifResult.order?.status)}>{notifResult.order?.status}</Badge></p>
                        <p><span className="text-muted-foreground">City:</span> {notifResult.order?.city_name || "Not assigned"}</p>
                        <p><span className="text-muted-foreground">Pickup:</span> {notifResult.order?.pickup}</p>
                        <p><span className="text-muted-foreground">Has Driver:</span> {notifResult.order?.has_driver_assigned ? "Yes" : "No"}</p>
                      </div>
                    </div>

                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Rider Pool Analysis</h3>
                      <div className="text-sm space-y-1">
                        <p><span className="text-muted-foreground">Total Approved Drivers:</span> {notifResult.rider_pool?.total_approved_drivers}</p>
                        <p><span className="text-muted-foreground">Food Delivery Enabled:</span> {notifResult.rider_pool?.food_delivery_enabled_riders}</p>
                        <p><span className="text-muted-foreground">Online Food Riders:</span> {notifResult.rider_pool?.online_food_riders}</p>
                        <p><span className="text-muted-foreground">Within 15km:</span> {notifResult.rider_pool?.riders_within_15km}</p>
                        <p className="font-medium"><span className="text-muted-foreground">Eligible to Notify:</span> {notifResult.rider_pool?.eligible_to_notify}</p>
                      </div>
                    </div>
                  </div>

                  {/* Nearby Riders Detail */}
                  {notifResult.nearby_riders_detail?.length > 0 && (
                    <div>
                      <h3 className="font-semibold mb-2">Nearby Riders Analysis</h3>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {notifResult.nearby_riders_detail.map((rider, idx) => (
                          <div key={idx} className={`p-3 border rounded-lg flex items-center justify-between ${rider.would_be_notified ? "border-green-500 bg-green-50 dark:bg-green-900/20" : ""}`}>
                            <div>
                              <span className="font-medium">{rider.name}</span>
                              <span className="text-sm text-muted-foreground ml-2">{rider.phone}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm">{rider.distance_km} km</span>
                              {rider.would_be_notified ? (
                                <Badge className="bg-green-500">Would Notify</Badge>
                              ) : (
                                <Badge variant="secondary">
                                  {!rider.within_15km ? "Too far" : !rider.food_delivery_approved && !rider.city_freelance_enabled ? "Not approved" : "No"}
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {notifResult?.error && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <p className="text-red-600">{notifResult.error}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Search Orders Tab */}
        <TabsContent value="search">
          <Card>
            <CardHeader>
              <CardTitle>Search Orders</CardTitle>
              <CardDescription>Find orders by customer name, restaurant, city, or phone number</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Customer Name</Label>
                  <Input
                    placeholder="e.g., Bharath"
                    value={searchParams.customer_name}
                    onChange={(e) => setSearchParams({...searchParams, customer_name: e.target.value})}
                    data-testid="search-customer-input"
                  />
                </div>
                <div>
                  <Label>Restaurant Name</Label>
                  <Input
                    placeholder="e.g., A1 Hotel"
                    value={searchParams.restaurant_name}
                    onChange={(e) => setSearchParams({...searchParams, restaurant_name: e.target.value})}
                    data-testid="search-restaurant-input"
                  />
                </div>
                <div>
                  <Label>City Name</Label>
                  <Input
                    placeholder="e.g., Sedam"
                    value={searchParams.city_name}
                    onChange={(e) => setSearchParams({...searchParams, city_name: e.target.value})}
                    data-testid="search-city-input"
                  />
                </div>
                <div>
                  <Label>Phone Number</Label>
                  <Input
                    placeholder="e.g., 9999999999"
                    value={searchParams.phone}
                    onChange={(e) => setSearchParams({...searchParams, phone: e.target.value})}
                    data-testid="search-phone-input"
                  />
                </div>
              </div>

              <Button onClick={searchOrders} disabled={loading} data-testid="search-orders-btn">
                {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                Search Orders
              </Button>

              {searchResults && (
                <div className="mt-4">
                  <p className="text-sm text-muted-foreground mb-2">Found {searchResults.orders_found} orders</p>
                  
                  {searchResults.orders_found === 0 ? (
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-center">
                      <p className="text-yellow-600">No orders found matching your search criteria</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {searchResults.orders.map((order, idx) => (
                        <div key={idx} className="p-3 border rounded-lg flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium">{order.ride_id || order.id}</span>
                              <Badge className={getStatusColor(order.status)}>{order.status}</Badge>
                              <Badge variant="outline">{order.city_name || "No City"}</Badge>
                            </div>
                            <div className="text-sm text-muted-foreground mt-1">
                              <span className="flex items-center gap-1">
                                <Building2 className="w-3 h-3" /> {order.sourceLocationName}
                              </span>
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {order.customerName || order.customer?.name} - {order.customerPhone || order.customer?.phone}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-medium">₹{order.offerRate}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(order.createdAt || order.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* City Check Tab */}
        <TabsContent value="city">
          <Card>
            <CardHeader>
              <CardTitle>City Assignment Check</CardTitle>
              <CardDescription>Check which city a location belongs to and detect overlapping cities</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <Label>Latitude</Label>
                  <Input
                    type="number"
                    step="any"
                    placeholder="e.g., 17.385"
                    value={cityCheckCoords.lat}
                    onChange={(e) => setCityCheckCoords({...cityCheckCoords, lat: e.target.value})}
                    data-testid="city-lat-input"
                  />
                </div>
                <div className="flex-1">
                  <Label>Longitude</Label>
                  <Input
                    type="number"
                    step="any"
                    placeholder="e.g., 78.486"
                    value={cityCheckCoords.lng}
                    onChange={(e) => setCityCheckCoords({...cityCheckCoords, lng: e.target.value})}
                    data-testid="city-lng-input"
                  />
                </div>
                <Button onClick={checkCity} disabled={loading} data-testid="check-city-btn">
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                  <span className="ml-2">Check</span>
                </Button>
              </div>

              {cityCheckResult && (
                <div className="mt-4 space-y-4">
                  {/* Warning for overlapping cities */}
                  {cityCheckResult.warning && (
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                      <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400">
                        <AlertTriangle className="w-5 h-5" />
                        <span>{cityCheckResult.warning}</span>
                      </div>
                    </div>
                  )}

                  {/* Assigned City */}
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <div className="flex items-center gap-2 text-green-600 dark:text-green-400 font-medium">
                      <CheckCircle className="w-5 h-5" />
                      <span>Orders at this location will be assigned to:</span>
                    </div>
                    <p className="mt-2 text-lg font-bold">
                      {cityCheckResult.assigned_city?.name || "No city (outside all service areas)"}
                    </p>
                  </div>

                  {/* Overlapping Cities */}
                  {cityCheckResult.overlapping_cities?.length > 0 && (
                    <div>
                      <h3 className="font-semibold mb-2">
                        All Cities Covering This Location ({cityCheckResult.overlapping_cities_count})
                      </h3>
                      <div className="space-y-2">
                        {cityCheckResult.overlapping_cities.map((city, idx) => (
                          <div 
                            key={idx} 
                            className={`p-3 border rounded-lg flex items-center justify-between ${city.would_be_assigned ? "border-green-500 bg-green-50 dark:bg-green-900/20" : ""}`}
                          >
                            <div>
                              <span className="font-medium">{city.city_name}</span>
                              {city.would_be_assigned && (
                                <Badge className="ml-2 bg-green-500">Will be assigned</Badge>
                              )}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              <span>{city.distance_from_center_km} km from center</span>
                              <span className="mx-2">•</span>
                              <span>Radius: {city.service_radius_km} km</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rider Check Tab */}
        <TabsContent value="riders">
          <Card>
            <CardHeader>
              <CardTitle>Rider Availability Check</CardTitle>
              <CardDescription>Find available food delivery riders near a location</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4 items-end flex-wrap">
                <div className="flex-1 min-w-[150px]">
                  <Label>Latitude</Label>
                  <Input
                    type="number"
                    step="any"
                    placeholder="e.g., 17.385"
                    value={riderCheckCoords.lat}
                    onChange={(e) => setRiderCheckCoords({...riderCheckCoords, lat: e.target.value})}
                    data-testid="rider-lat-input"
                  />
                </div>
                <div className="flex-1 min-w-[150px]">
                  <Label>Longitude</Label>
                  <Input
                    type="number"
                    step="any"
                    placeholder="e.g., 78.486"
                    value={riderCheckCoords.lng}
                    onChange={(e) => setRiderCheckCoords({...riderCheckCoords, lng: e.target.value})}
                    data-testid="rider-lng-input"
                  />
                </div>
                <div className="w-24">
                  <Label>Radius (km)</Label>
                  <Input
                    type="number"
                    placeholder="15"
                    value={riderCheckCoords.radius}
                    onChange={(e) => setRiderCheckCoords({...riderCheckCoords, radius: e.target.value})}
                    data-testid="rider-radius-input"
                  />
                </div>
                <Button onClick={checkRiders} disabled={loading} data-testid="check-riders-btn">
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
                  <span className="ml-2">Check</span>
                </Button>
              </div>

              {riderCheckResult && (
                <div className="mt-4 space-y-4">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-600">{riderCheckResult.summary.eligible_nearby_riders}</p>
                      <p className="text-sm text-muted-foreground">Eligible Nearby</p>
                    </div>
                    <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg text-center">
                      <p className="text-2xl font-bold text-gray-600">{riderCheckResult.summary.offline_riders}</p>
                      <p className="text-sm text-muted-foreground">Offline</p>
                    </div>
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-center">
                      <p className="text-2xl font-bold text-red-600">{riderCheckResult.summary.blocked_riders}</p>
                      <p className="text-sm text-muted-foreground">Blocked</p>
                    </div>
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-center">
                      <p className="text-2xl font-bold text-yellow-600">{riderCheckResult.summary.not_approved_riders}</p>
                      <p className="text-sm text-muted-foreground">Not Approved</p>
                    </div>
                  </div>

                  {/* Eligible Riders List */}
                  {riderCheckResult.eligible_riders?.length > 0 ? (
                    <div>
                      <h3 className="font-semibold mb-2">Eligible Riders (will receive order)</h3>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {riderCheckResult.eligible_riders.map((rider, idx) => (
                          <div key={idx} className="p-3 border rounded-lg flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-full">
                                <Bike className="w-4 h-4 text-green-600" />
                              </div>
                              <div>
                                <p className="font-medium">{rider.name}</p>
                                <p className="text-sm text-muted-foreground">{rider.phone}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-medium">{rider.distance_km} km away</p>
                              {rider.approved_via === "city_freelance" && (
                                <Badge variant="outline" className="text-xs">Via Freelance</Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                      <div className="flex items-center gap-2 text-yellow-600">
                        <AlertTriangle className="w-5 h-5" />
                        <span>No eligible riders found within {riderCheckCoords.radius} km</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-2">
                        Reasons: {riderCheckResult.summary.offline_riders} offline, {riderCheckResult.summary.not_approved_riders} not approved, {riderCheckResult.summary.blocked_riders} blocked
                      </p>
                    </div>
                  )}

                  {/* Exclusion Reasons */}
                  {(riderCheckResult.reasons_for_exclusion?.offline?.length > 0 || 
                    riderCheckResult.reasons_for_exclusion?.not_approved?.length > 0) && (
                    <div className="p-4 bg-muted rounded-lg">
                      <h4 className="font-medium mb-2">Excluded Riders</h4>
                      <div className="text-sm space-y-1">
                        {riderCheckResult.reasons_for_exclusion.offline?.length > 0 && (
                          <p><span className="text-muted-foreground">Offline:</span> {riderCheckResult.reasons_for_exclusion.offline.join(", ")}</p>
                        )}
                        {riderCheckResult.reasons_for_exclusion.not_approved?.length > 0 && (
                          <p><span className="text-muted-foreground">Not Approved:</span> {riderCheckResult.reasons_for_exclusion.not_approved.join(", ")}</p>
                        )}
                        {riderCheckResult.reasons_for_exclusion.blocked?.length > 0 && (
                          <p><span className="text-muted-foreground">Blocked:</span> {riderCheckResult.reasons_for_exclusion.blocked.join(", ")}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default OrderDebugPage;
